import { Grid } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { VariableGroupsDropdown } from "components/Campaign/CampaignConditions/VariableGroupsDropdown/VariableGroupsDropdown";
import { MultiInput } from "components/FormControls/MultiInput/MultiInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { Field, FieldProps } from "formik";
import { useMessage } from "hooks/intl";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    input: {
      "& input": {
        backgroundColor: theme.white50,
      },

      width: "100%",
    },
    multiInput: {
      width: "100%",
      maxWidth: "unset",
      "& div > div": {
        backgroundColor: theme.white50,
      },
    },
  }),
);

const singleValueConditionOperators = {
  isEqual: { id: "is_equal", label: "is_equal" },
  isNotEqual: { id: "is_not_equal", label: "is_not_equal" },
  matchesRegex: { id: "matches_regex", label: "matches_regex" },
  endsWith: { id: "ends_with", label: "ends_with" },
  startsWith: { id: "starts_with", label: "starts_with" },
};

const multiValueConditionOperators = {
  isNotOneOf: { id: "is_not_one_of", label: "is_not_one_of" },
  isOneOf: { id: "is_one_of", label: "is_one_of" },
};

const variableGroupsConditionOperators = {
  isOneOfGroupValues: {
    id: "is_one_of_group_values",
    label: "is_one_of_collections",
  },
  isNotOneOfGroupValues: {
    id: "is_not_one_of_group_values",
    label: "is_not_one_of_collections",
  },
};

type SelectInputWithOperatorsProps = {
  status?: any;
  formFieldName: string;
  inputName: string;
  valueField: string;
  currentOperator: string;
  className?: string;
  isAchievementForm?: boolean;
  shouldSuppressHj?: boolean;
};

export function SelectInputWithOperators({
  status,
  inputName,
  valueField,
  formFieldName,
  currentOperator,
  className,
  isAchievementForm,
  shouldSuppressHj,
}: SelectInputWithOperatorsProps) {
  const classes = useStyles();
  const { message } = useMessage();

  const { isEqual, isNotEqual, matchesRegex, endsWith, startsWith } =
    singleValueConditionOperators;
  const { isNotOneOf, isOneOf } = multiValueConditionOperators;
  const { isOneOfGroupValues, isNotOneOfGroupValues } =
    variableGroupsConditionOperators;
  const conditionItems = [
    isEqual,
    isNotEqual,
    isNotOneOf,
    isOneOf,
    ...(isAchievementForm ? [] : [isOneOfGroupValues, isNotOneOfGroupValues]),
    matchesRegex,
    endsWith,
    startsWith,
  ].map((item) => ({
    id: item.id,
    label: message({ id: item.label }),
  }));

  const getOperatorsId = <
    T extends { [key: string]: { id: string; label: string } },
  >(
    ...objs: T[]
  ) => objs.flatMap((obj) => Object.values(obj).map((operator) => operator.id));

  const isTextInputActive = getOperatorsId(
    singleValueConditionOperators,
  ).includes(currentOperator);

  const isMultiInputActive = (currentOperator: string) =>
    getOperatorsId(multiValueConditionOperators).includes(currentOperator);

  const isValuesGroupActive = (currentOperator: string) =>
    getOperatorsId(variableGroupsConditionOperators).includes(currentOperator);

  return (
    <Field name={`${formFieldName}.${inputName}`}>
      {({ form, field }: FieldProps) => {
        return (
          <>
            <Grid item xs={6}>
              <SelectInput
                className={className || classes.input}
                width="100%"
                items={conditionItems}
                label={
                  <RequiredFieldLabel text={message({ id: "condition" })} />
                }
                name={`${formFieldName}.operator`}
                onChange={(event) => {
                  const newValue = isMultiInputActive(
                    event.target.value as string,
                  )
                    ? []
                    : null;
                  form.setFieldValue(valueField, newValue);
                }}
                data-test-id="condition-operator"
              />
            </Grid>
            {isTextInputActive && (
              <Grid item xs={12}>
                <TextInput
                  className={classes.input}
                  name={valueField}
                  label={<RequiredFieldLabel text={message({ id: "value" })} />}
                  data-test-id="condition-value"
                  data-hj-suppress={shouldSuppressHj}
                />
              </Grid>
            )}
            {isMultiInputActive(currentOperator) && (
              <Grid item xs={12}>
                <MultiInput
                  label=""
                  className={classes.multiInput}
                  formField={valueField as string}
                  helperText={message({ id: "multiInputHelper" })}
                  placeholder={message({ id: "add" })}
                  status={status}
                  data-test-id="condition-value"
                />
              </Grid>
            )}
            {isValuesGroupActive(currentOperator) && (
              <Grid item xs={12}>
                <VariableGroupsDropdown
                  fieldName={`${valueField}.groupOfValues`}
                  data-test-id="condition-value"
                  placeholder={message({ id: "chooseFromTheList" })}
                  value={field.value?.groupOfValues}
                />
              </Grid>
            )}
          </>
        );
      }}
    </Field>
  );
}
